import { createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';

export interface CompanyMap {
    [companyId: string]: string;
}

export interface ResellerMap {
    [resellerCompanyId: string]: string;
}

export interface LocationMap {
    [locationCompanyId: string]: string;
}

export interface ResellerByCompanyMap {
    [companyId: string]: {
        resellerCompanyId: number;
        resellerName: string;
    }[];
}

export interface LocationByCompanyMap {
    [companyId: string]: {
        locationId: number;
        locationName: string;
    }[];
}

export interface OrgState {
    'custom:Companies': CompanyMap;
    'custom:Resellers': ResellerMap;
    'custom:Locations': LocationMap;
    resellerByCompany: ResellerByCompanyMap;
    locationByCompany: LocationByCompanyMap;
}

const initialState = {
    'custom:Companies': {},
    'custom:Resellers': {},
    'custom:Locations': {},
    resellerByCompany: {},
    locationByCompany: {},
};

export const orgMapSlice = createSlice({
    name: 'orgMap',
    initialState,
    reducers: {
        setCompanies: (state: OrgState, action: PayloadAction<CompanyMap>) => {
            state['custom:Companies'] = action.payload;
        },
        setResellers: (state: OrgState, action: PayloadAction<ResellerByCompanyMap>) => {
            state['resellerByCompany'] = action.payload;
            /** Flatten the Map Keyed by parents to K/V for direct lookups */
            state['custom:Resellers'] = Object.keys(action.payload).reduce((accPrime, payloadKey) => {
                const flattenedKeys = action.payload[payloadKey].reduce(
                    (accSec, { resellerCompanyId, resellerName }) => {
                        accSec[resellerCompanyId.toString()] = resellerName;
                        return accSec;
                    },
                    {} as ResellerMap
                );
                return {
                    ...accPrime,
                    ...flattenedKeys,
                };
            }, {} as ResellerMap);
        },
        setLocations: (state: OrgState, action: PayloadAction<LocationByCompanyMap>) => {
            state['locationByCompany'] = action.payload;
            /** Flatten the Map Keyed by parents to K/V for direct lookups
             *  ToDo: With Advent of Org Stack, deliver simple KV mappings -
             *  Take token or API key, return appropriate structure.
             *
             */
            state['custom:Locations'] = Object.keys(action.payload).reduce((accPrime, payloadKey) => {
                const flattenedKeys = action.payload[payloadKey].reduce((accSec, { locationId, locationName }) => {
                    accSec[locationId.toString()] = locationName;
                    return accSec;
                }, {} as LocationMap);
                return {
                    ...accPrime,
                    ...flattenedKeys,
                };
            }, {} as LocationMap);
        },
        resetOrg: () => {
            return initialState;
        },
    },
});

export const { setCompanies, setResellers, setLocations, resetOrg } = orgMapSlice.actions;
export default orgMapSlice.reducer;
